import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import RecommendList from "@/pages/detail/components/RecommendList";
import { mediaList } from "@/mocks/media";
import type { MediaItem } from "@/types/media";
import { findMediaById, formatViews, getEpisodeCount, getRecommendations, getTypeLabel } from "@/utils/media";

type PlayerStatus = "loading" | "playing" | "error";

export default function PlayPage() {
  const { id, episode } = useParams();
  const navigate = useNavigate();
  const all = mediaList as MediaItem[];
  const item = findMediaById(all, id);

  const [status, setStatus] = useState<PlayerStatus>("loading");

  const total = item ? getEpisodeCount(item) : 1;
  const rawEpisode = Number(episode);
  const currentEpisode = Number.isFinite(rawEpisode) ? Math.min(Math.max(Math.trunc(rawEpisode), 1), total) : 1;

  const startPlayback = useCallback(() => {
    setStatus("loading");
    const timer = window.setTimeout(() => setStatus("playing"), 1100);
    return timer;
  }, []);

  useEffect(() => {
    const timer = startPlayback();
    return () => window.clearTimeout(timer);
  }, [startPlayback, id, currentEpisode]);

  if (!item) {
    return (
      <PageLayout title="播放页 - 桔子TV" description="未找到对应的影视内容。">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-24 text-center">
          <span className="w-20 h-20 mx-auto rounded-full bg-background-200 flex items-center justify-center">
            <i className="ri-error-warning-line text-4xl text-foreground-500" />
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mt-5">影片不存在或已下架</h1>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer">
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </PageLayout>
    );
  }

  const recommendations = getRecommendations(all, item, 5);
  const episodeNumbers = Array.from({ length: total }, (_, i) => i + 1);

  return (
    <PageLayout
      title={`${item.title} 第${currentEpisode}集在线观看 - 桔子TV`}
      description={`${item.title}第${currentEpisode}集免费在线观看，${item.description.slice(0, 40)}`}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-6 md:py-8">
        <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-600 mb-4 flex-wrap" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/category/${item.type}`} className="hover:text-primary-500 transition-colors cursor-pointer">{getTypeLabel(item.type)}</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/detail/${item.id}`} className="hover:text-primary-500 transition-colors cursor-pointer">{item.title}</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-950">第{currentEpisode}集</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-5">
            <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-background-950 border border-background-200">
              <img
                src={item.banner || item.cover}
                alt={`${item.title} 第${currentEpisode}集`}
                className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${status === "playing" ? "opacity-70" : "opacity-45"}`}
              />
              <div className="absolute inset-0 bg-background-950/50" />

              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                {status === "loading" && (
                  <div className="animate-fade-in">
                    <span className="w-14 h-14 mx-auto rounded-full border-[3px] border-background-500 border-t-primary-500 animate-spin-slow" />
                    <p className="mt-4 text-sm text-foreground-800">加载中…</p>
                  </div>
                )}
                {status === "playing" && (
                  <div className="animate-fade-in">
                    <span className="w-16 h-16 mx-auto rounded-full bg-primary-500 text-background-50 flex items-center justify-center">
                      <i className="ri-play-fill text-3xl" />
                    </span>
                    <p className="mt-4 text-sm text-foreground-950 font-medium">正在播放：{item.title} 第{currentEpisode}集</p>
                    <p className="mt-1 text-xs text-foreground-600">示范播放 · 实际播放功能待接入</p>
                  </div>
                )}
                {status === "error" && (
                  <div className="animate-fade-in">
                    <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-accent-400 flex items-center justify-center">
                      <i className="ri-error-warning-line text-3xl" />
                    </span>
                    <p className="mt-4 text-sm text-foreground-950 font-medium">播放出错了</p>
                    <p className="mt-1 text-xs text-foreground-600">演示错误状态 · 请重试或切换其他集数</p>
                    <button
                      type="button"
                      onClick={startPlayback}
                      className="mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                    >
                      <i className="ri-refresh-line" />
                      重新加载
                    </button>
                  </div>
                )}
              </div>

              <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-background-950/70 backdrop-blur text-[11px] text-foreground-800 font-label">
                演示播放器 · 无真实视频源
              </span>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="font-heading text-lg md:text-2xl font-bold text-foreground-950 line-clamp-1">
                    {item.title} · 第{currentEpisode}集
                  </h1>
                  <p className="text-xs text-foreground-600 mt-1.5">
                    {getTypeLabel(item.type)} · {item.year} · {item.region} · {item.updateStatus}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    disabled={currentEpisode <= 1}
                    onClick={() => navigate(`/play/${item.id}/${currentEpisode - 1}`)}
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-background-200/60 border border-background-300 text-sm text-foreground-800 whitespace-nowrap transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400"
                  >
                    <i className="ri-skip-back-line" />
                    上一集
                  </button>
                  <button
                    type="button"
                    disabled={currentEpisode >= total}
                    onClick={() => navigate(`/play/${item.id}/${currentEpisode + 1}`)}
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-background-200/60 border border-background-300 text-sm text-foreground-800 whitespace-nowrap transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400"
                  >
                    下一集
                    <i className="ri-skip-forward-line" />
                  </button>
                  <Link
                    to={`/detail/${item.id}`}
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line" />
                    详情页
                  </Link>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setStatus("error")}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-background-300 text-xs text-foreground-600 hover:border-accent-400 hover:text-accent-400 whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-bug-line" />
                  模拟播放异常
                </button>
                <span className="text-[11px] text-foreground-600">
                  播放 {formatViews(item.views)} · 评分 {item.rating.toFixed(1)}
                </span>
              </div>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-primary-500" />
                选集
                <span className="text-xs text-foreground-600 font-normal">共 {total} 集</span>
              </h2>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                {episodeNumbers.map((num) => (
                  <Link
                    key={num}
                    to={`/play/${item.id}/${num}`}
                    className={`shrink-0 w-11 h-11 rounded-md flex items-center justify-center text-sm font-label transition-colors cursor-pointer border ${
                      num === currentEpisode
                        ? "bg-primary-500 border-primary-500 text-background-50 font-semibold"
                        : "bg-background-200/60 border-background-200 text-foreground-700 hover:border-primary-400 hover:text-foreground-950"
                    }`}
                  >
                    {num}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-primary-500" />
                剧情简介
              </h2>
              <p className="text-sm text-foreground-700 leading-loose line-clamp-4">{item.description}</p>
            </div>
          </div>

          <aside className="space-y-5">
            <section className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-accent-500" />
                相关推荐
              </h2>
              <RecommendList items={recommendations} />
            </section>
          </aside>
        </div>
      </div>
    </PageLayout>
  );
}